import { Box, CircularProgress, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { requestWithUserAuth } from '../../../api/auth'
import { loadingOff } from '../../../state/navigation'
import { useThemeMode } from '../../../theme'

export const QRDisplay = ({ tableNumber, size = 200 }) => {
  const dispatch = useDispatch()
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)
  const { palette } = useThemeMode()
  const { t } = useTranslation()
  const [blobUrl, setBlobUrl] = useState(null)
  const [error, setError] = useState(false)

  const isFixed = typeof size === 'number'
  const containerSx = {
    textAlign: 'center',
    padding: '16px',
    border: `1px solid ${palette.paperColorDivider}`,
    borderRadius: '8px',
    display: isFixed ? 'inline-block' : 'block',
    ...(isFixed ? {} : { width: size })
  }
  const imgSx = isFixed
    ? { width: size, height: size, display: 'block', margin: '0 auto' }
    : { width: '100%', aspectRatio: '1 / 1', display: 'block', margin: '0 auto' }
  const placeholderSx = isFixed
    ? { width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { width: '100%', aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center' }

  const endpoint = tableNumber != null
    ? `${config?.publicBackendUrl}/${config?.apiUri}/voting/snacks/registrations/table/${tableNumber}/qr`
    : `${config?.publicBackendUrl}/${config?.apiUri}/voting/snacks/registrations/me/qr`

  useEffect(() => {
    if (!endpoint || !auth?.authToken) return
    let objectUrl = null
    let cancelled = false
    setError(false)
    setBlobUrl(null)

    requestWithUserAuth(config, auth, (updatedAuth) => {
      if (cancelled) return
      fetch(endpoint, { headers: { Authorization: updatedAuth.accessToken } })
        .then((res) => {
          if (!res.ok) throw new Error(res.status)
          return res.blob()
        })
        .then((blob) => {
          if (!cancelled) {
            objectUrl = URL.createObjectURL(blob)
            setBlobUrl(objectUrl)
          }
        })
        .catch(() => { if (!cancelled) setError(true) })
        .finally(() => { dispatch(loadingOff()) })
    })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, auth?.authToken])

  return (
    <Box sx={containerSx}>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 1, color: palette.textColor, opacity: 0.7 }}
      >
        {tableNumber != null ? t('snacks-qr-for-table', { number: tableNumber }) : t('snacks-qr-my-table')}
      </Typography>

      {!blobUrl && !error && (
        <Box sx={placeholderSx}>
          <CircularProgress sx={{ color: palette.headerColor }} />
        </Box>
      )}

      {blobUrl && (
        <Box
          component="img"
          src={blobUrl}
          alt={t('snacks-qr-alt')}
          sx={imgSx}
        />
      )}

      {error && (
        <Box sx={placeholderSx}>
          <Typography variant="caption" color="error">{t('snacks-qr-error')}</Typography>
        </Box>
      )}
    </Box>
  )
}
